/**
 * Edge Function Repository
 * 
 * Repository pattern implementation for Supabase Edge Functions.
 * Provides a clean, testable interface for calling edge functions.
 * 
 * Design Pattern: Repository Pattern
 * - Abstracts data access logic
 * - Provides consistent error handling
 * - Easy to mock for testing
 * - Centralized configuration
 */

import { supabase, config } from '../config/supabase';

/**
 * Base class for Edge Function calls
 * Handles common functionality like error handling, logging, and retries
 */
class EdgeFunctionRepository {
  /**
   * Call a Supabase Edge Function
   * 
   * @param {string} functionName - Name of the edge function
   * @param {Object} payload - Request payload
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Response data
   * @throws {Error} If the request fails
   */
  async invoke(functionName, payload = {}, options = {}) {
    const {
      headers = {},
      retries = 0,
    } = options;

    // Validate Supabase configuration
    if (!config.hasValidConfig) {
      throw new Error(
        'Supabase is not configured. Please set environment variables.'
      );
    }

    try {
      console.log(`[EdgeFunction] Calling ${functionName}`, payload);

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      if (error) {
        console.error(`[EdgeFunction] Error calling ${functionName}:`, error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      console.log(`[EdgeFunction] Success ${functionName}`, data);
      return data;
    } catch (error) {
      console.error(`[EdgeFunction] Failed to call ${functionName}:`, error);

      // Retry logic
      if (retries > 0) {
        console.log(`[EdgeFunction] Retrying ${functionName} (${retries} attempts left)`);
        return this.invoke(functionName, payload, { ...options, retries: retries - 1 });
      }

      throw error;
    }
  }

  /**
   * Call edge function with GET semantics
   * For GET requests, params are sent as query parameters in the URL
   */
  async get(functionName, params = {}, options = {}) {
    try {
      console.log(`[EdgeFunction] Calling ${functionName} (GET)`, params);

      // Build query string from params
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `${functionName}?${queryString}` : functionName;

      const { data, error } = await supabase.functions.invoke(url, {
        method: 'GET',
      });

      if (error) {
        console.error(`[EdgeFunction] Error calling ${functionName}:`, error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      console.log(`[EdgeFunction] Success ${functionName}`, data);
      return data;
    } catch (error) {
      console.error(`[EdgeFunction] Failed to call ${functionName}:`, error);

      // Retry logic
      if (options.retries > 0) {
        console.log(`[EdgeFunction] Retrying ${functionName} (${options.retries} attempts left)`);
        return this.get(functionName, params, { ...options, retries: options.retries - 1 });
      }

      throw error;
    }
  }

  /**
   * Call edge function with POST semantics
   */
  async post(functionName, payload = {}, options = {}) {
    return this.invoke(functionName, payload, { ...options, method: 'POST' });
  }
}

// Export singleton instance
const edgeFunctionRepository = new EdgeFunctionRepository();
export default edgeFunctionRepository;

